import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { bookmarksAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Bookmark, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function BookmarksPage() {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user) loadBookmarks(); else setLoading(false); }, [user]);

  const loadBookmarks = async () => {
    try {
      const res = await bookmarksAPI.list({});
      setBookmarks(res.data.bookmarks || []);
    } catch {}
    finally { setLoading(false); }
  };

  const removeBookmark = async (articleId) => {
    try {
      await bookmarksAPI.remove(articleId);
      toast.success('Removed from reading list');
      loadBookmarks();
    } catch { toast.error('Failed'); }
  };

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10" data-testid="bookmarks-page">
          <div className="flex items-center gap-3 mb-8">
            <Bookmark className="w-6 h-6 text-[#B89B5E]" />
            <h1 className="news-masthead text-3xl font-bold">Reading List</h1>
          </div>

          {!user ? (
            <div className="bg-white rounded-xl border border-[#E7E0D6] p-12 text-center">
              <Bookmark className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
              <p className="text-[#5B616E] mb-4">Sign in to save articles to your reading list</p>
              <Link to="/login"><Button className="bg-[#121316] text-white hover:bg-[#1B1D22]">Sign In</Button></Link>
            </div>
          ) : loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
          ) : bookmarks.length > 0 ? (
            <div className="space-y-4">
              {bookmarks.map(bm => (
                <div key={bm.id} className="bg-white rounded-xl border border-[#E7E0D6] p-5 flex items-start gap-4 news-card" data-testid="bookmark-item">
                  {bm.article?.featured_image && (
                    <img src={bm.article.featured_image} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <Link to={`/article/${bm.article_id}`} className="font-semibold news-masthead hover:underline line-clamp-2">{bm.article?.title || 'Article'}</Link>
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#5B616E]">
                      {bm.article?.author_name && <span>{bm.article.author_name}</span>}
                      {bm.article?.created_at && <span>{new Date(bm.article.created_at).toLocaleDateString()}</span>}
                    </div>
                    {bm.notes && <p className="text-xs text-[#5B616E] mt-1 italic">{bm.notes}</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Link to={`/article/${bm.article_id}`}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><ExternalLink className="w-4 h-4" /></Button>
                    </Link>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#B3261E]" onClick={() => removeBookmark(bm.article_id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E7E0D6] p-12 text-center">
              <Bookmark className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
              <p className="text-[#5B616E]">No saved articles yet</p>
              <p className="text-xs text-[#5B616E] mt-1">Bookmark articles while reading to save them here</p>
            </div>
          )}
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
